/**
 * DashboardPage — panoramica KPI, pipeline, trend e lead recenti.
 * ★ Fase 59.P2 — split componenti oversize: filtri, tiles, grafici e liste
 * estratti in src/components/dashboard/ (DashboardFiltersBar, KpiTiles,
 * StageGridCard, DashboardCharts, RecentLeadsCard, dashboardUtils).
 * Comportamento e data-testid INVARIATI.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import TimeInStageCard from '../components/TimeInStageCard';
import CollectionsDashboardCard from '../components/CollectionsDashboardCard';
import ProposalsPipelineCard from '../components/ProposalsPipelineCard';
import MonthlyPaymentsCard from '../components/MonthlyPaymentsCard';
import VisitsWeeklyChart from '../components/VisitsWeeklyChart';
import MyCommissionsCard from '../components/MyCommissionsCard';
import MonthlyIncomeCard from '../components/MonthlyIncomeCard';
import UnderperformanceAlertBanner from '../components/UnderperformanceAlertBanner';
import {
  ArrowUpRight, TrendingUp, Users, MessageSquare, Sparkles, CheckCircle2,
  Percent, FileText, Euro,
} from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';
import { todayIso, daysAgoIso } from '../components/dashboard/dashboardUtils';
import { DashboardFiltersBar } from '../components/dashboard/DashboardFiltersBar';
import { KpiTiles, StageGridCard } from '../components/dashboard/KpiTiles';
import {
  RevenueTargetsRow, TrendChartCard, FunnelCard, StageBarProfileRow,
  SourceSplitCard,
} from '../components/dashboard/DashboardCharts';
import { RecentLeadsCard } from '../components/dashboard/RecentLeadsCard';
// ★ Fase 12.B — Obiettivi mensili team (Direttrice ES + admin su tenant ES)
import { SalesGoalsCard } from '../components/dashboard/SalesGoalsCard';
// ★ Fase 14 — Classifica mensile venditori del team
import { TeamLeaderboardCard } from '../components/dashboard/TeamLeaderboardCard';
// ★ Fase 33 — Dashboard Carriera ES per livello (Setter/Junior/Senior)
import { CareerSection } from '../components/career/CareerSection';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdminLike = ['admin', 'amministrativo'].includes(user?.role);

  // ── Period / filter state ──
  const [granularity, setGranularity] = useState('daily');
  const [startDate, setStartDate] = useState(daysAgoIso(30));
  const [endDate, setEndDate] = useState(todayIso());
  const [agentFilter, setAgentFilter] = useState(''); // empty = all
  const [agents, setAgents] = useState([]);
  // ★ 2026-07-30 — date_mode toggle: 'origin' (created_at) vs 'activity'
  // (any stage transition). Persisted in localStorage so user preference
  // survives navigation.
  const [dateMode, setDateMode] = useState(() => {
    try {
      const v = localStorage.getItem('gp_dash_date_mode');
      return v === 'origin' || v === 'activity' ? v : 'activity';
    } catch { return 'activity'; }
  });
  useEffect(() => {
    try { localStorage.setItem('gp_dash_date_mode', dateMode); } catch { /* no-op */ }
  }, [dateMode]);

  // ── Data ──
  const [kpi, setKpi] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canExport, setCanExport] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ★ 2026-07-03: refresh mechanism
  // - `refreshTick` è incrementato ad ogni refresh manuale/auto e passato come
  //   dependency alla ProposalsPipelineCard per forzarne il ricaricamento.
  // - `lastRefreshAt` mostra all'utente quando ha ricaricato l'ultima volta.
  // - `autoRefresh` (localStorage) abilita polling ogni 60s.
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    try { return localStorage.getItem('gp_dash_auto_refresh') === '1'; } catch { return false; }
  });

  // Load agents (admin only) + export permission
  useEffect(() => {
    if (isAdminLike) {
      api.get('/agents').then((r) => setAgents(r.data || [])).catch(() => {});
    }
    api.get('/kpis/export/_permission').then((r) => setCanExport(!!r.data?.allowed)).catch(() => {});
  }, [isAdminLike]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        granularity,
        date_mode: dateMode,
      });
      if (agentFilter) params.append('agent_id', agentFilter);

      // ★ 2026-07-03: "Lead recenti" ora rispetta il range di date del filtro.
      // ★ 2026-07-30: usa il campo che corrisponde al date_mode selezionato.
      const leadsParams = new URLSearchParams({
        limit: '8',
        start_date: startDate,
        end_date: endDate,
        date_field: dateMode === 'origin' ? 'created_at' : 'activity',
      });
      const [k, l] = await Promise.all([
        api.get(`/kpis?${params.toString()}`).then((r) => r.data),
        api.get(`/leads?${leadsParams.toString()}`).then((r) => r.data),
      ]);
      setKpi(k);
      setRecent(l);
      setLastRefreshAt(new Date());
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, granularity, agentFilter, dateMode]);

  useEffect(() => { load(); }, [load]);

  // ★ 2026-07-03: Auto-refresh on window focus / tab visible.
  // Se l'utente sposta un lead in pipeline in un'altra tab, poi torna sulla
  // Dashboard: i numeri si aggiornano automaticamente.
  useEffect(() => {
    let debounceId = null;
    const trigger = () => {
      if (debounceId) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(() => {
        setRefreshTick((tk) => tk + 1);
        load();
      }, 250);
    };
    const onFocus = () => trigger();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') trigger();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      if (debounceId) window.clearTimeout(debounceId);
    };
  }, [load]);

  // ★ 2026-07-03: Auto-refresh timer opzionale (60s) — persistito.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = window.setInterval(() => {
      setRefreshTick((tk) => tk + 1);
      load();
    }, 60000);
    return () => window.clearInterval(id);
  }, [autoRefresh, load]);

  const handleManualRefresh = useCallback(() => {
    setRefreshTick((tk) => tk + 1);
    load();
    toast.success(t('dashboard.toastUpdated', 'Dashboard aggiornata'));
  }, [load]);

  const toggleAutoRefresh = useCallback(() => {
    setAutoRefresh((prev) => {
      const next = !prev;
      try { localStorage.setItem('gp_dash_auto_refresh', next ? '1' : '0'); } catch { /* noop */ }
      toast.info(next
        ? t('dashboard.autoRefreshOn', 'Auto-refresh attivo (ogni 60s)')
        : t('dashboard.autoRefreshOff', 'Auto-refresh disattivato'));
      return next;
    });
  }, []);

  const setQuickRange = (range) => {
    setStartDate(daysAgoIso(range.days));
    setEndDate(todayIso());
    setGranularity(range.granularity);
  };

  const downloadReport = async (format) => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        format,
        start_date: startDate,
        end_date: endDate,
        granularity,
        date_mode: dateMode,
      });
      if (agentFilter) params.append('agent_id', agentFilter);

      // Use blob response to download
      const res = await api.get(`/kpis/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'xlsx' ? 'xlsx' : 'pdf';
      const agentLabel = agentFilter
        ? '_' + ((agents.find((a) => a.id === agentFilter)?.name || '').replace(/\s+/g, '_'))
        : '';
      link.setAttribute('download', `KPI_${granularity}_${startDate}_${endDate}${agentLabel}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Report ${format.toUpperCase()} scaricato`);
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 403
        ? t('dashboard.reportForbidden', 'Solo Richard, Morena, Eufemia o Claudia possono scaricare i report.')
        : t('dashboard.reportError', 'Errore download report');
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  const tiles = useMemo(() => {
    if (!kpi) return [];
    const tot = kpi.totals || {};
    return [
      { label: agentFilter ? t('dashboard.kpi.agentLeads', "Lead dell'agente") : (isAdminLike ? t('dashboard.kpi.totalLeads', 'Lead totali') : t('dashboard.kpi.myLeads', 'I miei lead')), value: kpi.total_leads, icon: Users },
      { label: t('dashboard.kpi.appointments', 'Appuntamenti'), value: tot.reached_appuntamento || 0, icon: MessageSquare },
      { label: t('dashboard.kpi.shows', 'Show fatti'), value: tot.reached_show || 0, icon: CheckCircle2 },
      { label: t('dashboard.kpi.closed', 'Chiusi'), value: tot.reached_chiuso || 0, icon: Sparkles },
    ];
  }, [kpi, isAdminLike, agentFilter, t]);

  // ★ 2026-09-05 (Fase 30) — KPI card agenti richieste dall'utente:
  // Lead→Show, Risposta→Show, Show→Chiusura, Totale proposte €, Fatturato €
  const agentKpiTiles = useMemo(() => {
    const ak = kpi?.agent_kpis;
    if (!ak) return [];
    const eur = (v) => `€ ${Math.round(v || 0).toLocaleString('it-IT')}`;
    return [
      {
        label: t('dashboard.kpi.leadToShow', 'Lead → Show'),
        value: `${ak.lead_to_show_pct ?? 0}%`,
        sub: `${ak.lead_to_show_num || 0} show su ${ak.lead_to_show_den || 0} lead`,
        icon: Percent,
      },
      {
        label: t('dashboard.kpi.rispostaToShow', 'Risposta → Show'),
        value: `${ak.risposta_to_show_pct ?? 0}%`,
        sub: `${ak.lead_to_show_num || 0} show su ${ak.risposta_to_show_den || 0} risposte`,
        icon: TrendingUp,
      },
      {
        label: t('dashboard.kpi.showToClose', 'Show → Chiusura'),
        value: `${ak.show_to_close_pct ?? 0}%`,
        sub: `${ak.show_to_close_num || 0} chiusi su ${ak.lead_to_show_num || 0} show`,
        icon: CheckCircle2,
      },
      {
        label: t('dashboard.kpi.proposalsTotal', 'Totale proposte'),
        value: eur(ak.proposals_total_eur),
        sub: `${ak.proposals_count || 0} ordini proposta`,
        icon: FileText,
      },
      {
        label: t('dashboard.kpi.revenue', 'Fatturato'),
        value: eur(ak.revenue_eur),
        sub: t('dashboard.kpi.revenueSub', 'chiuso cliente + partner'),
        icon: Euro,
      },
    ];
  }, [kpi, t]);

  const stageBars = useMemo(() => {
    if (!kpi?.stages) return [];
    return kpi.stages.map((s) => ({ stage: s.label, count: s.count || 0, color: s.color }));
  }, [kpi]);

  const profileTotal = useMemo(() => {
    const p = kpi?.profile_counts || {};
    return (p.equity || 0) + (p.real_estate || 0) + (p.both || 0);
  }, [kpi]);

  const selectedAgentLabel = useMemo(() => {
    if (!agentFilter) return null;
    return agents.find((a) => a.id === agentFilter)?.name || agentFilter;
  }, [agentFilter, agents]);

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-5 fade-up">
      {/* ★ 2026-08-02 (Fase 30 F2) — Banner underperformance per Associate */}
      <UnderperformanceAlertBanner />
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-[hsl(var(--primary))]" data-testid="dashboard-title">
            {t('dashboard.greetingHi', 'Buongiorno')}{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedAgentLabel ? (
              <Trans i18nKey="dashboard.greetingAnalyzing" values={{ name: selectedAgentLabel }} components={{ b: <b /> }} />
            ) : isAdminLike ? (
              <>{t('dashboard.subtitleAdmin', 'Panoramica generale pipeline · Green Paradise / HCF Property')}</>
            ) : (
              <>{t('dashboard.subtitlePersonal', 'I tuoi numeri personali')}</>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/pipeline"><Button variant="secondary" data-testid="go-pipeline-button">{t('dashboard.openPipeline', 'Apri Pipeline')}</Button></Link>
          <Link to="/app/leads">
            <Button data-testid="go-leads-button">
              {isAdminLike ? t('dashboard.allLeads', 'Tutti i Lead') : t('dashboard.myLeads', 'I miei Lead')} <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters bar */}
      <DashboardFiltersBar
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        granularity={granularity}
        setGranularity={setGranularity}
        dateMode={dateMode}
        setDateMode={setDateMode}
        agentFilter={agentFilter}
        setAgentFilter={setAgentFilter}
        agents={agents}
        isAdminLike={isAdminLike}
        canExport={canExport}
        downloading={downloading}
        downloadReport={downloadReport}
        setQuickRange={setQuickRange}
        selectedAgentLabel={selectedAgentLabel}
        lastRefreshAt={lastRefreshAt}
        loading={loading}
        handleManualRefresh={handleManualRefresh}
        autoRefresh={autoRefresh}
        toggleAutoRefresh={toggleAutoRefresh}
      />

      {/* ★ Fase 33 — Carrera ES: pannello personale (agenti) / team (direttori) */}
      <CareerSection />

      {/* ★ Fase 12.B/14 — Obiettivi mensili + Classifica del team (Sofia · ES) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SalesGoalsCard />
        <TeamLeaderboardCard />
      </div>

      {/* KPI Tiles */}
      <KpiTiles tiles={tiles} />

      {/* ★ 2026-09-05 (Fase 30) — KPI conversioni + proposte + fatturato */}
      {agentKpiTiles.length > 0 && (
        <KpiTiles
          tiles={agentKpiTiles}
          gridClassName="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
        />
      )}

      {/* ★ 2026-07-29 — Stage Grid con TUTTI gli stage cliccabili (filtro Lead) */}
      <StageGridCard stages={kpi?.stages} agentFilter={agentFilter} />

      {/* 2026-06-25: Collections / cashflow card (admin only) */}
      {isAdminLike && <CollectionsDashboardCard />}

      {/* ★ 2026-08-04 (Fase 34) — Incassi mese proposte (visibile a tutti, scope per ruolo) */}
      <MonthlyPaymentsCard />

      {/* ★ 2026-08-13 (Fase 46) — Le mie commissioni (solo non-admin) */}
      {!isAdminLike && <MyCommissionsCard />}

      {/* ★ 2026-08-14 — Incasso del Mese + Fatture in scadenza (TUTTI gli agenti) */}
      <MonthlyIncomeCard />

      {/* ★ 2026-08-11 (Fase 37) — Trend visite landing (solo admin) */}
      {isAdminLike && <VisitsWeeklyChart defaultDays={7} />}

      {/* ★ 2026-07-03: Proposte inviate — potenziale fatturato del periodo */}
      <ProposalsPipelineCard
        startDate={startDate}
        endDate={endDate}
        agentId={agentFilter || null}
        refreshTick={refreshTick}
      />

      {/* ★ 2026-07-30 — Tempo medio permanenza in stage (health-check pipeline) */}
      <TimeInStageCard
        startDate={startDate}
        endDate={endDate}
        dateMode={dateMode}
        agentId={agentFilter || null}
        refreshTick={refreshTick}
      />

      {/* Revenue + Conversion targets */}
      <RevenueTargetsRow kpi={kpi} />

      {/* Trend chart (granularity-aware) */}
      <TrendChartCard series={kpi?.series} granularity={granularity} />

      {/* Funnel */}
      <FunnelCard funnel={kpi?.funnel} />

      {/* Stage bar chart + Profile breakdown */}
      <StageBarProfileRow stageBars={stageBars} kpi={kpi} profileTotal={profileTotal} />

      {/* ★ Fase 62 — Split sorgenti traffico (by_source) */}
      <SourceSplitCard bySource={kpi?.by_source} />

      {/* Recent leads */}
      <RecentLeadsCard recent={recent} loading={loading} />

      {isAdminLike && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5 text-amber-700" />
              <div>
                <div className="font-medium">{t('dashboard.leaderboard', 'Leaderboard team')}</div>
                <div className="text-xs text-muted-foreground">
                  Conversion-rate by Associate (target 45%) e Senior (target 8%)
                </div>
              </div>
            </div>
            <Link to="/app/agents">
              <Button variant="outline" size="sm" data-testid="dashboard-agents-link">
                {t('dashboard.seeAgents', 'Vedi agenti')} <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
